import { getRandomIntInclusive } from '@application/utils';
import {
  CardItemContainer,
  CardItemWrapper,
  Container,
  Section,
  SideLayoutSideWrapper,
  SideLayoutWrapper,
  SkeletonBox,
} from '@presentation/components';
import { Skeleton as CardItemBreederSkeleton } from '@presentation/components/CardItemBreeder/components/Skeleton';
import { Skeleton as CardItemLitterSkeleton } from '@presentation/components/CardItemLitter/components/Skeleton';
import type { FC } from 'react';
import { Fragment, useCallback, useMemo } from 'react';
import * as RootStyled from '../../styled';
import * as Styled from './styled';

export const PageSkeleton: FC = () => {
  const skeletons = useMemo(() => {
    return {
      header: {
        tags: Array(getRandomIntInclusive(1, 3))
          .fill(undefined)
          .map(() => ({
            width: getRandomIntInclusive(40, 70),
            height: 20,
          })),
      },
      sideSection: () => ({
        title: {
          width: `${getRandomIntInclusive(50, 90)}%`,
          height: 35,
          marginBottom: 10,
        },
        items: Array(getRandomIntInclusive(3, 5))
          .fill(undefined)
          .map(() => ({
            label: { width: `${getRandomIntInclusive(20, 40)}%`, height: 20 },
            value: { width: `${getRandomIntInclusive(20, 40)}%`, height: 20, marginLeft: 'auto' },
          })),
      }),
      section: () => ({
        title: {
          maxWidth: getRandomIntInclusive(250, 350),
        },
        texts: Array(getRandomIntInclusive(2, 3))
          .fill(undefined)
          .map(() => {
            const topLines = getRandomIntInclusive(1, 2);
            return {
              top: { width: '90%', height: topLines * 21.5, display: 'block', marginBottom: 10 },
              bottom: {
                width: `${getRandomIntInclusive(40, 80)}%`,
                height: 21.5,
                marginBottom: 20,
              },
            };
          }),
      }),
    };
  }, []);

  const skeletonSideSection = useCallback(() => {
    const section = skeletons.sideSection();
    return (
      <Section>
        <SkeletonBox style={{ ...section.title }} />
        {section.items.map((item, index) => (
          <Styled.SideItemWrapper key={index}>
            <SkeletonBox style={{ ...item.label }} />
            <SkeletonBox style={{ ...item.value }} />
          </Styled.SideItemWrapper>
        ))}
      </Section>
    );
  }, [skeletons]);

  const skeletonSection = useCallback(() => {
    const section = skeletons.section();

    return (
      <Section>
        <Styled.SectionTitleSkeleton style={{ ...section.title }} />
        {section.texts.map((text, index) => (
          <Fragment key={index}>
            <SkeletonBox style={{ ...text.top }} />
            <SkeletonBox style={{ ...text.bottom }} />
          </Fragment>
        ))}
      </Section>
    );
  }, [skeletons]);

  return (
    <>
      <Styled.Header />
      <Container>
        <SideLayoutWrapper>
          <SideLayoutSideWrapper>
            <RootStyled.SideContentWrapper>
              <RootStyled.ImageWrapperContainer>
                <Styled.MainImageSkeleton />
              </RootStyled.ImageWrapperContainer>
              <Styled.SidePadding>
                {skeletonSideSection()}
                {skeletonSideSection()}
                {skeletonSideSection()}
              </Styled.SidePadding>
            </RootStyled.SideContentWrapper>
            <Styled.TopMargin>
              <RootStyled.SideGeneticsWrapper>
                <Styled.SectionTitleSkeleton
                  style={{
                    width: `${getRandomIntInclusive(50, 90)}%`,
                    height: 35,
                    marginBottom: 10,
                  }}
                />
              </RootStyled.SideGeneticsWrapper>
            </Styled.TopMargin>
          </SideLayoutSideWrapper>
          <RootStyled.StyledSideLayoutContentWrapper>
            <Styled.TopMargin>
              {skeletonSection()}
              {skeletonSection()}
              {skeletonSection()}
            </Styled.TopMargin>
          </RootStyled.StyledSideLayoutContentWrapper>
        </SideLayoutWrapper>
      </Container>

      <RootStyled.SectionWrapperLitters>
        <Container>
          <Styled.RelatedSectionTitleSkeleton />

          <CardItemContainer>
            {Array(getRandomIntInclusive(3, 8))
              .fill(undefined)
              .map((_, index) => (
                <CardItemWrapper key={index}>
                  <CardItemLitterSkeleton />
                </CardItemWrapper>
              ))}
          </CardItemContainer>
        </Container>
      </RootStyled.SectionWrapperLitters>

      <RootStyled.SectionWrapperBreeders>
        <Container>
          <Styled.RelatedSectionTitleSkeleton />

          <CardItemContainer>
            {Array(getRandomIntInclusive(3, 8))
              .fill(undefined)
              .map((_, index) => (
                <CardItemWrapper key={index}>
                  <CardItemBreederSkeleton />
                </CardItemWrapper>
              ))}
          </CardItemContainer>
        </Container>
      </RootStyled.SectionWrapperBreeders>
    </>
  );
};
